import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ConversationChannel } from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  FileInfo,
  FileUploadError,
  FileUploadStatus,
  GALAXY_UPLOADER_SERVICE_TOKEN,
  GalaxyUploaderComponent,
} from '@vendasta/galaxy/uploader';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { firstValueFrom, map, Observable, ReplaySubject } from 'rxjs';
import { ConversationDetail, InboxService, ParticipantService } from '../../../../../../core/src/index';
import { MAX_FILES, MAX_RECORDING_TIME } from '../../../../../../core/src/lib/conversation.constants';
import { getMaxFileSizeInMB, InboxUploadService } from './inbox-upload.service';
import { ChatSourceId } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';

@Component({
  selector: 'inbox-upload-file',
  templateUrl: 'inbox-upload-file.component.html',
  styleUrls: ['./inbox-upload-file.component.scss'],
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useExisting: InboxUploadService }],
  standalone: false,
})
export class InboxUploadFileComponent implements OnDestroy {
  maxFiles = MAX_FILES;
  private readonly conversationChannel$$: ReplaySubject<ConversationChannel> = new ReplaySubject<ConversationChannel>(
    1,
  );
  conversationChannel$: Observable<ConversationChannel> = this.conversationChannel$$.asObservable();

  isRecording = false;
  stream: MediaStream | undefined;
  recorder?: MediaRecorder;
  chunks: any[] = [];
  recordingTimeout?: number;
  disableVoiceRecording$: Observable<boolean> = this.inboxUploadService.files$$.pipe(map((files) => files.length > 0));

  @ViewChild(GalaxyUploaderComponent) public readonly uploader: GalaxyUploaderComponent | undefined;

  @Input() set conversationDetail(value: ConversationDetail) {
    this.inboxUploadService.conversationId$$.next(value?.conversation?.conversationId);
    this.conversationChannel$$.next(value?.conversation?.channel);
  }

  @Input() selectedChatSourceId: ChatSourceId | undefined;

  @Input() enableVoiceRecording = false;

  @Output() fileChanged = new EventEmitter<FileInfo>();

  constructor(
    private readonly snackbarService: SnackbarService,
    private readonly inboxUploadService: InboxUploadService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly participantService: ParticipantService,
    private readonly inboxService: InboxService,
  ) {}

  public async fileUploaded(file: FileInfo): Promise<void> {
    this.emitAnalyticsEvent('file-upload-success', file);
    this.fileChanged.emit(file);
  }

  public async uploadErrored(err: FileUploadError): Promise<void> {
    if (err.error.message === 'GALAXY.UPLOADER.ERROR.MAX_FILES_EXCEEDED') {
      this.snackbarService.openErrorSnack('INBOX.UPLOAD_FILE.MAX_FILES_EXCEEDED');
    } else if (err.error.message === 'GALAXY.UPLOADER.ERROR.MAX_FILE_SIZE_EXCEEDED') {
      const channel = await firstValueFrom(this.conversationChannel$);
      this.snackbarService.openErrorSnack('INBOX.UPLOAD_FILE.MAX_FILE_SIZE_EXCEEDED_ARG', {
        interpolateTranslateParams: {
          maxFileSize: getMaxFileSizeInMB(this.selectedChatSourceId).toString(),
          channel: channel.toString(),
        },
      });
    } else {
      this.snackbarService.openErrorSnack(err.error.message);
    }
    this.emitAnalyticsEvent('file-upload-error', err.fileInfo, err.error.message);
  }

  public filesChanged(files: FileInfo[]): void {
    //Check the file status to avoid emitting the file twice when file is uploaded successfully
    if (files[0] && files[0].status !== FileUploadStatus.Success) {
      this.fileChanged.emit(files[0]);
    }
  }

  get showUploadIcon(): boolean {
    return this.selectedChatSourceId !== 'google';
  }

  toggleRecording() {
    this.isRecording = !this.isRecording;
    if (this.isRecording) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  startRecording() {
    this.emitAnalyticsEvent('voice-record-started');
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.stream = stream;
        this.recorder = new MediaRecorder(stream);
        this.recorder.ondataavailable = (ev) => this.chunks.push(ev.data);
        this.recorder.start();

        this.recordingTimeout = window.setTimeout(() => {
          this.stopRecording();
        }, MAX_RECORDING_TIME);
      })
      .catch((err) => {
        console.error('Could not start recording: ', err);
        this.isRecording = false;
      });
  }

  stopRecording() {
    if (!this.recorder) {
      console.error('No recording to stop');
      return;
    }

    const fileName = 'voice-' + new Date().toISOString() + '.webm';
    this.recorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: 'audio/webm' });
      this.chunks = [];

      const file = new File([blob], fileName, { type: blob.type });

      const fileInfo = {
        file: file,
        name: file.name,
        status: FileUploadStatus.Queued,
      } as FileInfo;
      this.inboxUploadService.setFiles([fileInfo]);
      this.inboxUploadService.uploadQueuedFiles();
      this.isRecording = false;
    };

    if (this.recordingTimeout) {
      window.clearTimeout(this.recordingTimeout);
    }

    this.recorder.stop();
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
  }

  async emitAnalyticsEvent(eventName: string, file?: FileInfo, err?: string) {
    const [sender, channel] = await Promise.all([
      firstValueFrom(this.participantService.currentParticipant$),
      firstValueFrom(this.conversationChannel$),
    ]);

    this.analyticsService.trackEvent('inbox', 'file-share', eventName, 0, {
      accountGroupId: sender.accountGroupId,
      partnerId: sender.partnerId,
      location: this.inboxService.platformLocation,
      channel,
      mediaType: file?.file?.type ?? '',
      error: err ?? '',
    });
  }

  ngOnDestroy() {
    if (this.recorder) {
      this.recorder.stop();
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
    this.inboxUploadService.clear();
    this.inboxUploadService.fileErrored$$.next(null);
  }
}
